import type { ReactElement, ReactNode } from 'react';
import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useDOI } from '@/contexts/DOIContext';
import { OnboardingContext } from '@/contexts/OnboardingContext';

const OnboardingModal = lazy(() =>
  import('@/components/onboarding/OnboardingModal').then((module) => ({ default: module.OnboardingModal }))
);
const ExtendedOnboardingModal = lazy(() => import('@/components/onboarding/ExtendedOnboardingModal'));

const OnboardingLoadingDialog = (): ReactElement => {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useLayoutEffect((): undefined | (() => void) => {
    const dialog = dialogRef.current;
    if (dialog === null) return undefined;
    dialog.showModal();
    return (): void => {
      dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-busy="true"
      aria-labelledby="onboarding-loading-label"
      aria-modal="true"
      className="m-0 h-dvh max-h-none w-screen max-w-none border-0 bg-black/95 p-0 text-white backdrop:bg-black/95"
      onCancel={(event) => {
        event.preventDefault();
      }}
    >
      <div className="flex min-h-dvh w-full items-center justify-center p-token-lg">
        <div className="flex flex-col items-center gap-token-md text-center" role="status" aria-live="polite">
          <span
            aria-hidden="true"
            className="h-12 w-12 rounded-full border-4 border-white/20 border-t-brand-primary motion-safe:animate-spin"
          />
          <p id="onboarding-loading-label" className="text-sm font-medium text-white/80">
            {t('loading')}
          </p>
        </div>
      </div>
    </dialog>
  );
};

export function OnboardingProvider({ children }: { children: ReactNode }): ReactElement {
  const { isAuthenticated, user, loading } = useAuth();
  const { isDOIRequired } = useDOI();
  const queryClient = useQueryClient();
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isExtendedOnboardingOpen, setIsExtendedOnboardingOpen] = useState(false);

  const { data: userProfile, refetch: refetchProfile } = useQuery({
    queryKey: ['user-profile-onboarding', user?.id],
    queryFn: async () => {
      const { userProfileApi } = await import('@/lib/api/user-profile');
      return userProfileApi.getMyProfile();
    },
    enabled: isAuthenticated && !loading && user !== null && !isDOIRequired,
    staleTime: 5 * 60 * 1000,
  });

  const { data: extendedOnboardingStatus } = useQuery({
    queryKey: ['extended-onboarding-status', user?.id],
    queryFn: async () => {
      const { extendedOnboardingApi } = await import('@/lib/api/extended-onboarding');
      return extendedOnboardingApi.getStatus();
    },
    enabled: isAuthenticated && !loading && user !== null && userProfile?.onboarding_completed === true,
    staleTime: 5 * 60 * 1000,
  });

  const isOnboardingRequired = Boolean(
    isAuthenticated && !loading && user !== null && !isDOIRequired && userProfile && !userProfile.onboarding_completed
  );
  const isExtendedOnboardingRequired = Boolean(
    isAuthenticated &&
    !loading &&
    user !== null &&
    userProfile?.onboarding_completed &&
    extendedOnboardingStatus?.extended_onboarding_enabled &&
    !extendedOnboardingStatus.onboarding_completed &&
    !extendedOnboardingStatus.onboarding_skipped
  );

  useEffect((): undefined | (() => void) => {
    if (!isOnboardingRequired) {
      setIsOnboardingOpen(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setIsOnboardingOpen(true);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [isOnboardingRequired]);

  useEffect((): undefined | (() => void) => {
    if (isExtendedOnboardingRequired && !isOnboardingOpen) {
      const timer = setTimeout(() => {
        setIsExtendedOnboardingOpen(true);
      }, 500);
      return () => {
        clearTimeout(timer);
      };
    }
    if (!isExtendedOnboardingRequired) setIsExtendedOnboardingOpen(false);
    return undefined;
  }, [isExtendedOnboardingRequired, isOnboardingOpen]);

  const handleOnboardingComplete = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['user-profile-onboarding'] });
    await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    await queryClient.invalidateQueries({ queryKey: ['extended-onboarding-status'] });
    await refetchProfile();
    await new Promise<void>((resolve) => setTimeout(resolve, 100));
    setIsOnboardingOpen(false);
  }, [queryClient, refetchProfile]);

  const handleExtendedOnboardingComplete = useCallback((): void => {
    setIsExtendedOnboardingOpen(false);
    void queryClient.invalidateQueries({ queryKey: ['extended-onboarding-status'] });
  }, [queryClient]);

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingRequired,
        isOnboardingOpen,
        isExtendedOnboardingOpen,
        userProfile: userProfile ?? null,
        refetchProfile,
      }}
    >
      {children}
      {isOnboardingOpen && (
        <Suspense fallback={<OnboardingLoadingDialog />}>
          <OnboardingModal
            open
            onComplete={() => {
              void handleOnboardingComplete();
            }}
          />
        </Suspense>
      )}
      {isExtendedOnboardingOpen && (
        <Suspense fallback={<OnboardingLoadingDialog />}>
          <ExtendedOnboardingModal
            open
            onComplete={handleExtendedOnboardingComplete}
            onSkip={handleExtendedOnboardingComplete}
          />
        </Suspense>
      )}
    </OnboardingContext.Provider>
  );
}
